import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { hasRole, getPrimaryRole, type AppRole } from '@/utils/roleHelpers';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  allowedUserTypes?: ('renter' | 'landlord')[];
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/login',
  allowedUserTypes
}) => {
  const { user, profile, loading, isInitialized } = useAuth();
  const location = useLocation();

  // Show loading while auth is initializing
  if (!isInitialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4">
            <img 
              src="/lovable-uploads/76503acc-0290-4b3e-ae3b-f6ab214ab2ce.png" 
              alt="Flamingo" 
              className="h-8 mx-auto mb-4"
            />
          </div>
          <LoadingSpinner />
          <p className="text-muted-foreground text-sm mt-2">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not logged in
  if (requireAuth && !user) {
    // Prevent redirect loops - don't redirect if already on auth pages
    const authPages = ['/login', '/signup', '/forgot-password', '/reset-password'];
    if (authPages.includes(location.pathname)) {
      return <>{children}</>;
    }
    
    console.log('[AuthGuard] Redirecting unauthenticated user to:', redirectTo);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If user is logged in but on auth pages, redirect to appropriate dashboard
  if (user && ['/login', '/signup'].includes(location.pathname)) {
    const primaryRole = getPrimaryRole(profile);
    const redirectPath = primaryRole === 'landlord' ? '/landlord-dashboard' : '/dashboard';
    
    if (import.meta.env.DEV) {
      console.log('[AuthGuard] Redirecting authenticated user to:', redirectPath);
    }
    return <Navigate to={redirectPath} replace />;
  }

  // Check role-based restrictions (secure authorization using user_roles table)
  if (allowedUserTypes && user && profile) {
    const allowedRoles = allowedUserTypes as AppRole[];
    const hasAllowedRole = allowedRoles.some(role => hasRole(profile, role));
    
    if (!hasAllowedRole) {
      if (import.meta.env.DEV) {
        console.log('[AuthGuard] User does not have required role. User roles:', (profile as any).roles, 'Required:', allowedRoles);
      }
      const primaryRole = getPrimaryRole(profile);
      const fallbackPath = primaryRole === 'landlord' ? '/landlord-dashboard' 
                         : primaryRole === 'renter' ? '/dashboard'
                         : '/'; // Safe fallback for unknown roles
      
      // Prevent self-redirect loop
      if (location.pathname === fallbackPath) {
        console.warn('[AuthGuard] Self-redirect prevented. Redirecting to home.');
        return <Navigate to="/" replace />;
      }
      
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
};